import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { UserModel } from './profile';

const MAX_RANGE_DAYS = 365;

// Shared daily usage row (user & organization endpoints)
export interface DailyUsageRow {
  usageDate: string;
  workspaceType?: string;
  messageCount: number;
  conversationCount: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costUSD: number;
}

// Tenant aggregated
export interface TenantDailyUsageRow {
  usageDate: string;
  messageCount: number;
  conversationCount: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costUSD: number;
}

// Tenant by user (groupBy=date,user or groupByUser=true)
export interface TenantByUserRow {
  usageDate: string;
  identityId: string | null;
  identityName: string | null;
  workspaceType: string;
  messageCount: number;
  conversationCount: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costUSD: number;
}

/** Tenant row when using groupBy (date + optional user, workspaceType, identityType) */
export interface TenantGroupedRow {
  usageDate: string;
  identityId?: string | null;
  identityName?: string | null;
  workspaceType?: string;
  identityType?: string | null;
  messageCount: number;
  conversationCount: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costUSD?: number;
}

export interface UserUsageResponse {
  data: DailyUsageRow[];
}

export interface OrganizationUsageResponse {
  data: DailyUsageRow[];
}

/** Organization usage query row (groupBy: date, tenantId, workspaceType; +identityName/identityType when tenant-scoped) */
export interface OrgUsageQueryRow {
  usageDate?: string;
  tenantId?: string;
  workspaceType?: string;
  identityId?: string | null;
  identityName?: string | null;
  identityType?: string | null;
  messageCount: number;
  conversationCount: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  costUSD?: number;
}

export interface OrganizationUsageQueryResponse {
  data: OrgUsageQueryRow[];
}

export interface TenantUsageResponse {
  data: TenantDailyUsageRow[] | TenantByUserRow[] | TenantGroupedRow[];
}

/** Tier limit info returned by the backend */
export interface TierLimit {
  limitTokens: number | null;
  usedTokens: number;
  remainingTokens: number | null;
  usedPercent: number | null;
  remainingPercent: number | null;
  isOverLimit: boolean;
  overByTokens: number;
}

export interface CategoryCount {
  totalMessages: number;
  totalConversations: number;
}

export interface UsageSummaryData {
  tenantId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  tier: {
    totalTokens: number;
    limit?: TierLimit;
  };
  summary: {
    totalMessages: number;
    totalConversations: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUSD?: number;
    questionsAsked?: CategoryCount;
    tasks?: CategoryCount;
  };
  dailyBreakdown: Array<{
    usageDate: string;
    messageCount: number;
    conversationCount: number;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    costUSD?: number;
  }>;
}

export interface UsageSummaryResponse {
  data: UsageSummaryData;
}

export interface OrgUsageSummaryData {
  organizationId: string;
  period: {
    startDate: string;
    endDate: string;
  };
  tier: {
    tierId?: string;
    tierName?: string;
    totalTokens: number;
    limit?: TierLimit;
  };
  summary: {
    totalMessages: number;
    totalConversations: number;
    totalTokens: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUSD?: number;
    questionsAsked?: CategoryCount;
    tasks?: CategoryCount;
  };
  dailyBreakdown: Array<{
    usageDate: string;
    messageCount: number;
    conversationCount: number;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    costUSD?: number;
  }>;
}

export interface OrgUsageSummaryResponse {
  data: OrgUsageSummaryData;
}

function toYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getDefaultMonthStart(): string {
  const d = new Date();
  d.setDate(1);
  return toYYYYMMDD(d);
}

function getToday(): string {
  return toYYYYMMDD(new Date());
}

function assertValidRange(startDate: string, endDate: string): void {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (start > end) {
    throw new Error('startDate must be before or equal to endDate');
  }
  const days = (end - start) / (1000 * 60 * 60 * 24);
  if (days > MAX_RANGE_DAYS) {
    throw new Error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`);
  }
}

export interface UserUsageParams {
  startDate: string;
  endDate: string;
  workspaceType?: string;
}

export async function fetchUserUsage(
  params: UserUsageParams,
): Promise<UserUsageResponse> {
  const { startDate, endDate, workspaceType } = params;
  assertValidRange(startDate, endDate);
  const query = new URLSearchParams({ startDate, endDate });
  if (workspaceType) {
    query.set('workspaceType', workspaceType);
  }
  const { data } = await api.get<UserUsageResponse>(
    `/api/core/pleri/usage/user?${query.toString()}`,
  );
  return data;
}

export interface OrganizationUsageParams {
  startDate: string;
  endDate: string;
  workspaceType?: string;
}

export async function fetchOrganizationUsage(
  params: OrganizationUsageParams,
): Promise<OrganizationUsageResponse> {
  const { startDate, endDate, workspaceType } = params;
  assertValidRange(startDate, endDate);
  const query = new URLSearchParams({ startDate, endDate });
  if (workspaceType) {
    query.set('workspaceType', workspaceType);
  }
  const { data } = await api.get<OrganizationUsageResponse>(
    `/api/core/pleri/usage/organization?${query.toString()}`,
  );
  return data;
}

export interface OrganizationUsageQueryParams {
  startDate: string;
  endDate: string;
  workspaceType?: string;
  /**
   * Comma-separated dimensions. Allowed: date, tenantId, workspaceType,
   * identityName, identityType. Default: date.
   */
  groupBy?: string;
  /** Constrain the query to a single tenant. */
  tenantId?: string;
  /** Constrain the query to a single user (pleri_usage.identityId). */
  identityId?: string;
}

export async function fetchOrganizationUsageQuery(
  params: OrganizationUsageQueryParams,
): Promise<OrganizationUsageQueryResponse> {
  const { startDate, endDate, workspaceType, groupBy, tenantId, identityId } =
    params;
  assertValidRange(startDate, endDate);
  const query = new URLSearchParams({ startDate, endDate });
  if (workspaceType) {
    query.set('workspaceType', workspaceType);
  }
  if (groupBy != null && groupBy !== '') {
    query.set('groupBy', groupBy);
  }
  if (tenantId != null && tenantId !== '') {
    query.set('tenantId', tenantId);
  }
  if (identityId != null && identityId !== '') {
    query.set('identityId', identityId);
  }
  const { data } = await api.get<OrganizationUsageQueryResponse>(
    `/api/core/pleri/usage/organization?${query.toString()}`,
  );
  return data;
}

export interface TenantUsageParams {
  startDate: string;
  endDate: string;
  workspaceType?: string;
  /** When true, equivalent to groupBy=date,identityName */
  groupByUser?: boolean;
  /** Comma-separated dimensions; date is always first. Allowed: date, identityName, workspaceType, identityType */
  groupBy?: string;
}

export async function fetchTenantUsage(
  params: TenantUsageParams,
): Promise<TenantUsageResponse> {
  const { startDate, endDate, workspaceType, groupByUser, groupBy } = params;
  assertValidRange(startDate, endDate);
  const query = new URLSearchParams({ startDate, endDate });
  if (workspaceType) {
    query.set('workspaceType', workspaceType);
  }
  if (groupBy != null && groupBy !== '') {
    query.set('groupBy', groupBy);
  } else if (groupByUser === true) {
    query.set('groupBy', 'date,identityName');
  }
  const { data } = await api.get<TenantUsageResponse>(
    `/api/core/pleri/usage/tenant?${query.toString()}`,
  );
  return data;
}

export interface UsageSummaryParams {
  startDate?: string;
  endDate?: string;
}

export async function fetchUsageSummary(
  params: UsageSummaryParams = {},
): Promise<UsageSummaryResponse> {
  const query = new URLSearchParams();
  if (params.startDate) {
    query.set('startDate', params.startDate);
  }
  if (params.endDate) {
    query.set('endDate', params.endDate);
  }
  const url =
    query.toString() === ''
      ? '/api/core/pleri/usage/summary'
      : `/api/core/pleri/usage/summary?${query.toString()}`;
  const { data } = await api.get<UsageSummaryResponse>(url);
  return data;
}

export async function fetchOrganizationUsageSummary(
  params: UsageSummaryParams = {},
): Promise<OrgUsageSummaryResponse> {
  const query = new URLSearchParams();
  if (params.startDate) {
    query.set('startDate', params.startDate);
  }
  if (params.endDate) {
    query.set('endDate', params.endDate);
  }
  const url =
    query.toString() === ''
      ? '/api/core/pleri/usage/summary/organization'
      : `/api/core/pleri/usage/summary/organization?${query.toString()}`;
  const { data } = await api.get<OrgUsageSummaryResponse>(url);
  return data;
}

export function useUserUsage(params: UserUsageParams, enabled = true) {
  return useQuery({
    queryKey: [
      'pleri-usage-user',
      params.startDate,
      params.endDate,
      params.workspaceType,
    ],
    queryFn: () => fetchUserUsage(params),
    enabled:
      enabled &&
      !!params.startDate &&
      !!params.endDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.startDate) &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.endDate),
    staleTime: 1000 * 60 * 2,
  });
}

export function useOrganizationUsage(
  params: OrganizationUsageParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'pleri-usage-organization',
      params.startDate,
      params.endDate,
      params.workspaceType,
    ],
    queryFn: () => fetchOrganizationUsage(params),
    enabled:
      enabled &&
      !!params.startDate &&
      !!params.endDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.startDate) &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.endDate),
    staleTime: 1000 * 60 * 2,
  });
}

export function useOrganizationUsageQuery(
  params: OrganizationUsageQueryParams,
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'pleri-usage-organization-query',
      params.startDate,
      params.endDate,
      params.workspaceType,
      params.groupBy,
      params.tenantId,
      params.identityId,
    ],
    queryFn: () => fetchOrganizationUsageQuery(params),
    enabled:
      enabled &&
      !!params.startDate &&
      !!params.endDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.startDate) &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.endDate),
    staleTime: 1000 * 60 * 2,
  });
}


export function useOrganizationUsageSummary(
  params: UsageSummaryParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: [
      'pleri-usage-summary-organization',
      params.startDate,
      params.endDate,
    ],
    queryFn: () => fetchOrganizationUsageSummary(params),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
}

export function useTenantUsage(params: TenantUsageParams, enabled = true) {
  return useQuery({
    queryKey: [
      'pleri-usage-tenant',
      params.startDate,
      params.endDate,
      params.workspaceType,
      params.groupByUser,
      params.groupBy,
    ],
    queryFn: () => fetchTenantUsage(params),
    enabled:
      enabled &&
      !!params.startDate &&
      !!params.endDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.startDate) &&
      /^\d{4}-\d{2}-\d{2}$/.test(params.endDate),
    staleTime: 1000 * 60 * 2,
  });
}

export function useUsageSummary(
  params: UsageSummaryParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['pleri-usage-summary', params.startDate, params.endDate],
    queryFn: () => fetchUsageSummary(params),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
}

export interface TenantModel {
  tenantId: string;
  name: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  status?: string;
}

export function useOrgTenants() {
  return useQuery({
    queryKey: ['listOrgTenants'],
    queryFn: () =>
      api
        .get<{ data: TenantModel[] }>('/api/base/tenants')
        .then((res) => res.data.data),
    staleTime: 1000 * 60 * 5,
  });
}

export { getDefaultMonthStart, getToday, toYYYYMMDD, MAX_RANGE_DAYS };

export interface OrgUsersResponse {
  data: UserModel[];
  meta?: { total?: number };
}

export function useOrgUsers() {
  return useQuery({
    queryKey: ['orgUsers'],
    queryFn: () =>
      api
        .get<OrgUsersResponse>('/api/base/v2/users?perPage=200')
        .then((res) => res.data.data),
    staleTime: 1000 * 60 * 5,
  });
}

export interface TenantLimitConfig {
  tenantId: string;
  limitTokens: number | null;
  setAt: number;
}

export async function listTenantLimits(): Promise<TenantLimitConfig[]> {
  const res = await api.get<{ data: TenantLimitConfig[] }>(
    '/api/core/pleri/usage/tenant-limits',
  );
  return res.data.data;
}

export async function setTenantLimit(payload: {
  tenantId: string;
  limitTokens: number;
}): Promise<void> {
  await api.post('/api/core/pleri/usage/tenant-limits', payload);
}

export async function deleteTenantLimit(tenantId: string): Promise<void> {
  await api.delete(`/api/core/pleri/usage/tenant-limits/${tenantId}`);
}

export function useTenantLimits() {
  return useQuery({
    queryKey: ['tenantLimits'],
    queryFn: listTenantLimits,
    staleTime: 1000 * 60 * 2,
  });
}

export function useSetTenantLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTenantLimit,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenantLimits'] });
    },
  });
}

export function useDeleteTenantLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTenantLimit,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenantLimits'] });
    },
  });
}
